import { Injectable } from '@angular/core';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { connectState } from '@examdojo/angular/util';
import { TopicLevel1Query, TopicLevel1UIModel, TopicLevel2UIModel } from '@examdojo/category/v2';
import { LocalizedStringCellRendererComponent, LocalizeService } from '@examdojo/core/i18n';
import { PlatformService } from '@examdojo/platform';
import {
  AbstractSimpleAgGridService,
  ExternalFilterType,
  ListViewColDef,
  ListViewExternalCheckboxFilterValue,
} from '@examdojo/ui/list-view';
import {
  LocalizedMarkdownStringCellRendererComponent,
  TopicPictureCellRendererComponent,
  TopicPictureCellValue,
} from '@examdojo/ui/list-view/cell-renderer';
import {
  GridOptions,
  GroupCellRendererParams,
  InitialGroupOrderComparatorParams,
  IRowNode,
  KeyCreatorParams,
  RowHeightParams,
  SelectionChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';

@Injectable()
export class AllTopicListViewAgGridService extends AbstractSimpleAgGridService<TopicLevel2UIModel> {
  constructor(
    private readonly topicLevel1Query: TopicLevel1Query,
    private readonly localizeService: LocalizeService,
    private readonly platformService: PlatformService,
  ) {
    super();
  }

  readonly state = connectState({
    isMobile: this.platformService.isSmall$,
  });

  readonly gridOptions: GridOptions<TopicLevel2UIModel> = {
    suppressCellFocus: true,
    suppressContextMenu: true,
    rowBuffer: 10, // Reduce if you don't need as much buffer
    suppressRowVirtualisation: false,
    groupDisplayType: 'groupRows',
    initialGroupOrderComparator: (params: InitialGroupOrderComparatorParams<TopicLevel2UIModel>) => {
      return (
        (params.nodeA.allLeafChildren?.[0]?.data?.topicLevel1?.sort_order ?? 0) -
        (params.nodeB.allLeafChildren?.[0]?.data?.topicLevel1?.sort_order ?? 0)
      );
    },
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No topics found',
      body: 'Please try with another filter',
    },
    getRowHeight: (params: RowHeightParams<TopicLevel2UIModel>) => (params.node.group ? 40 : 60),
    getRowClass: (params: { data?: TopicLevel2UIModel }) => {
      const baseClasses = 'cursor-pointer';
      if (params.data && !params.data.active) {
        return `${baseClasses} topic-inactive`;
      }
      return baseClasses;
    },
    headerHeight: this.state.isMobile ? 0 : undefined,
    animateRows: true,
    defaultColDef: {
      flex: 1,
      suppressMovable: true,
      menuTabs: [],
      sortable: false,
      floatingFilter: false,
      suppressHeaderMenuButton: true,
      resizable: false,
    },
    groupMaintainOrder: true,
    isRowSelectable: (node: IRowNode<TopicLevel2UIModel>): boolean => {
      return node.data?.active ?? false;
    },
    onSelectionChanged: (event: SelectionChangedEvent<TopicLevel2UIModel>) => {
      const renderedGroupRows = event.api.getRenderedNodes().filter((node) => node.group);

      event.api.redrawRows({ rowNodes: renderedGroupRows });
    },
    groupRowRendererParams: {
      checkbox: true,
      suppressCount: true,
      selectAll: 'all',
      mode: 'multiRow',
      checkboxLocation: 'autoGroupColumn',
      innerRenderer: (params: GroupCellRendererParams<TopicLevel2UIModel>) => {
        const selectedCount =
          params.node.allLeafChildren?.filter((child: IRowNode<TopicLevel2UIModel>) => child.isSelected()).length ?? 0;
        const totalCount = params.node.allLeafChildren?.length ?? 0;
        const topicLevel1Name = this.topicLevel1Query.getEntity(+params.value)?.name;
        return `<div class="flex items-center gap-2">
          <span class="text-md font-semibold text-secondary">${topicLevel1Name}</span>
          <span class="text-sm text-disabled ml-2">${selectedCount} of ${totalCount} selected</span>
        </div>`;
      },
    },
    groupDefaultExpanded: -1,
    rowSelection: 'multiple',
    rowMultiSelectWithClick: true,
    groupSelectsChildren: true,
    groupSelectsFiltered: false,
  };

  readonly columnDefs: Array<ListViewColDef<TopicLevel2UIModel>> = [
    {
      colId: 'topicLevel1',
      field: 'topicLevel1',
      headerName: 'TL1',
      hide: true,
      rowGroup: true,
      keyCreator: (params: KeyCreatorParams<TopicLevel2UIModel, TopicLevel1UIModel>) => `${params.value?.id}`,
      filterValueGetter: (params: ValueGetterParams<TopicLevel2UIModel>) => params.data?.topicLevel1?.name ?? '',
      comparator: (valueA, valueB) => {
        const topicLevel1A = valueA ? this.topicLevel1Query.getEntity(+valueA) : null;
        const topicLevel1B = valueB ? this.topicLevel1Query.getEntity(+valueB) : null;

        if (!topicLevel1A || !topicLevel1B) {
          return 0;
        }

        return topicLevel1A.sort_order - topicLevel1B.sort_order;
      },
      valueFormatter: (params: ValueFormatterParams<TopicLevel2UIModel>) => {
        return this.topicLevel1Query.getEntity(+params.value)?.name ?? '';
      },
      externalFilter: {
        label: 'Topic',
        filter: ExternalFilterType.Checkbox,
        doesExternalFilterPassFunc:
          (value: ListViewExternalCheckboxFilterValue<string>) => (node: IRowNode<TopicLevel2UIModel>) => {
            return node.data?.topicLevel1?.name ? value.includes(node.data?.topicLevel1.name) : true;
          },
      },
    },
    {
      field: 'code',
      colId: 'code',
      checkboxSelection: true,
      sort: 'asc',
      valueGetter: (params: ValueGetterParams<TopicLevel2UIModel>): TopicPictureCellValue | undefined => {
        if (!params.data) {
          return undefined;
        }
        return {
          code: params.data.code,
          sort_order: params.data.sort_order,
          icon: params.data.icon,
          color: params.data.topicLevel1?.color_name ?? undefined,
        };
      },
      valueFormatter: (params: ValueFormatterParams<TopicLevel2UIModel>) => params.data?.code ?? '',
      comparator: (valueA, valueB) => {
        if (!valueA || !valueB) {
          return 0;
        }
        return valueA.sort_order - valueB.sort_order;
      },
      cellRenderer: TopicPictureCellRendererComponent,
      headerName: 'Topic',
      maxWidth: this.state.isMobile ? 150 : 220,
      cellStyle: {
        whiteSpace: 'pre-line',
        lineHeight: '1.2',
        padding: this.state.isMobile ? '10px 5px 10px 30px' : '10px 5px 10px 50px',
        display: 'flex',
        alignItems: 'center',
      },
      cellRendererParams: {
        normalDisplay: true,
      },
      filterValueGetter: (params: ValueGetterParams<TopicLevel2UIModel>) => params.data?.code,
      externalSortingOrder: [
        {
          sort: 'asc',
          label: 'ID: A-Z',
          initial: true,
        },
        {
          sort: 'desc',
          label: 'ID: Z-A',
        },
      ],
    },
    {
      field: 'short_name',
      colId: 'short_name',
      cellStyle: {
        whiteSpace: 'pre-line',
        lineHeight: '1.2',
        display: 'flex',
        alignItems: 'center',
      },
      headerName: 'Name',
      cellRenderer: LocalizedStringCellRendererComponent,
      externalSortingOrder: [
        {
          sort: 'asc',
          label: 'Name: A-Z',
        },
        {
          sort: 'desc',
          label: 'Name: Z-A',
        },
      ],
      autoHeight: true,
      maxWidth: 310,
      comparator: (valueA, valueB) => {
        const nameA = valueA ? this.localizeService.localizeOnce(valueA) : '';
        const nameB = valueB ? this.localizeService.localizeOnce(valueB) : '';

        return nameA.localeCompare(nameB);
      },
      filterValueGetter: (params: ValueGetterParams<TopicLevel2UIModel>) => params.data?.short_name ?? '',
    },
    {
      field: 'example',
      colId: 'example',
      hide: this.state.isMobile,
      flex: 1,
      cellStyle: {
        whiteSpace: 'pre-line',
        lineHeight: '1.2',
        display: 'flex',
        alignItems: 'center',
      },
      autoHeight: true,
      headerName: 'Example question',
      cellRenderer: LocalizedMarkdownStringCellRendererComponent,
      // externalSortingOrder: [
      //   {
      //     sort: 'asc',
      //     label: 'Name (A-Z)',
      //   },
      //   {
      //     sort: 'desc',
      //     label: 'Name (Z-A)',
      //   },
      // ],
      filterValueGetter: (params: ValueGetterParams<TopicLevel2UIModel>) => params.data?.example ?? '',
    },
  ];
}
